import React, { useEffect, useRef, useState } from 'react';
import { ResponsiveContainer, LineChart, Line, Tooltip } from 'recharts';
import './TradingSimDashboard.css';

// Leaderboard composition colors, in FIXED order — these are the first 7
// slots of the validated categorical palette (dataviz skill), used exactly
// in this order so any adjacent pair a team's holdings happen to produce is
// one that's been checked for colorblind-safe separation. The 8th slot
// (red) is deliberately left out of this set: it's reserved elsewhere for
// "loss/critical", and reusing it here for an ordinary holding would make a
// position look like a problem. Cash is NOT one of these slots — it's
// always the same neutral gray (--baseline), so "gray = uninvested" reads
// the same on every bar regardless of what anyone holds.
const HOLDING_SLOT_COUNT = 7;

// Polls the public, unauthenticated /api/display/state endpoint — no admin
// key involved, since this component is meant to render on a shared/
// projector screen where anyone can view page source.
const DISPLAY_ENDPOINT = '/api/display/state';
const POLL_INTERVAL_MS = 5000;
const NEWS_CYCLE_MS = 6000;
const TRANSITION_SLIDE_MS = 6000;

function formatCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatSignedCurrency(value) {
  const sign = value >= 0 ? '+' : '−';
  return `${sign}${formatCurrency(Math.abs(value))}`;
}

function formatSignedPct(pct) {
  if (pct === null || pct === undefined) return '';
  const sign = pct >= 0 ? '+' : '−';
  return `${sign}${Math.abs(pct).toFixed(1)}%`;
}

// A single trade's reasoning, quoted, with the action it took — used
// underneath a winner/loser/mover slide so the room can see what the agent
// said going in next to what actually happened.
function ReasoningQuote({ who, trade }) {
  if (!trade) return null;
  return (
    <div className="transition-quote">
      <div className="transition-quote-action">
        {who ? `${who} — ` : ''}{trade.action.toUpperCase()} {trade.quantity} {trade.ticker || ''}
      </div>
      {trade.reasoning && <div className="transition-quote-text">&ldquo;{trade.reasoning}&rdquo;</div>}
    </div>
  );
}

// Builds the ordered list of slides for a round summary: winner, then loser
// (skipped if the same team swept both), then the biggest mover. Each slide
// carries whatever reasoning quotes are available so the overlay can show
// the decision right next to its outcome.
function buildTransitionSlides(summary) {
  if (!summary) return [];
  const { biggest_winner: winner, biggest_loser: loser, biggest_mover: mover } = summary;
  const sameTeam = winner && loser && winner.team === loser.team;
  const slides = [];

  if (winner) {
    slides.push({
      key: 'winner',
      icon: '🏆',
      label: 'Biggest Winner',
      team: winner.team,
      valueClass: 'delta-up',
      value: `${formatSignedCurrency(winner.delta)}${winner.pct !== null ? ` (${formatSignedPct(winner.pct)})` : ''}`,
      // A submission can be several actions now (sell some, buy others) —
      // quote every one of them, same pattern as the mover's trades below.
      quotes: (winner.trades || []).map((t) => ({ who: null, trade: t })),
    });
  }
  if (loser && !sameTeam) {
    slides.push({
      key: 'loser',
      icon: '📉',
      label: 'Biggest Loser',
      team: loser.team,
      valueClass: 'delta-down',
      value: `${formatSignedCurrency(loser.delta)}${loser.pct !== null ? ` (${formatSignedPct(loser.pct)})` : ''}`,
      quotes: (loser.trades || []).map((t) => ({ who: null, trade: t })),
    });
  }
  if (mover) {
    slides.push({
      key: 'mover',
      icon: mover.direction === 'up' ? '🚀' : '🔥',
      label: 'Biggest Mover',
      team: `${mover.ticker} — ${mover.name}`,
      valueClass: mover.direction === 'up' ? 'delta-up' : 'delta-down',
      value: `${formatSignedPct(mover.pct_change)} this round`,
      quotes: (mover.trades || []).map((t) => ({ who: t.team, trade: t })),
    });
  }
  return slides;
}

function RoundTransitionOverlay({ summary, onDismiss }) {
  const slides = buildTransitionSlides(summary);
  const [index, setIndex] = useState(0);

  // Reset to the first slide every time a genuinely new summary comes in.
  useEffect(() => {
    setIndex(0);
  }, [summary]);

  useEffect(() => {
    if (slides.length === 0) return undefined;
    const isLast = index >= slides.length - 1;
    const id = setTimeout(() => {
      if (isLast) onDismiss();
      else setIndex((i) => i + 1);
    }, TRANSITION_SLIDE_MS);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, slides.length]);

  if (slides.length === 0) return null;
  const slide = slides[index];
  const isLast = index >= slides.length - 1;

  function handleClick() {
    if (isLast) onDismiss();
    else setIndex((i) => i + 1);
  }

  return (
    <div className="transition-overlay" onClick={handleClick}>
      <div className="transition-card">
        <div className="transition-label">
          Round Recap{slides.length > 1 ? ` · ${index + 1}/${slides.length}` : ''}
        </div>

        <div key={slide.key} className="transition-row transition-slide-in">
          <span className="transition-icon">{slide.icon}</span>
          <div>
            <div className="transition-sublabel">{slide.label}</div>
            <div className="transition-team">{slide.team}</div>
            <div className={`transition-value ${slide.valueClass}`}>{slide.value}</div>
          </div>
        </div>

        {slide.quotes.length === 0 && (
          <div className="transition-quote transition-quote-empty">No trade placed this round.</div>
        )}
        {slide.quotes.map((q, i) => (
          <ReasoningQuote key={i} who={q.who} trade={q.trade} />
        ))}

        {slides.length > 1 && (
          <div className="transition-dots">
            {slides.map((s, i) => (
              <span key={s.key} className={`transition-dot ${i === index ? 'active' : ''}`} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function formatDelta(history) {
  if (!history || history.length < 2) return null;
  const latest = history[history.length - 1];
  const previous = history[history.length - 2];
  if (previous === 0) return null;
  const pct = ((latest - previous) / previous) * 100;
  return pct;
}

function CompanyCard({ company }) {
  const chartData = company.history.map((price, i) => ({ round: i, price }));
  const delta = formatDelta(company.history);
  const isUp = delta !== null && delta >= 0;

  return (
    <div className="company-card">
      <div className="company-card-header">
        <div>
          <div className="company-ticker">{company.ticker}</div>
          <div className="company-name">{company.name}</div>
        </div>
        <div className="company-price-block">
          <div className="company-price">{formatCurrency(company.price)}</div>
          {delta !== null && (
            <div className={`company-delta ${isUp ? 'delta-up' : 'delta-down'}`}>
              <span className="delta-icon">{isUp ? '▲' : '▼'}</span>
              {Math.abs(delta).toFixed(1)}%
            </div>
          )}
        </div>
      </div>
      <div className="company-sparkline">
        <ResponsiveContainer width="100%" height={64}>
          <LineChart data={chartData} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
            <Tooltip
              contentStyle={{
                background: 'var(--surface-1)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                fontSize: 12,
              }}
              labelFormatter={(round) => `Round ${round}`}
              formatter={(value) => [formatCurrency(value), 'Price']}
            />
            <Line
              type="monotone"
              dataKey="price"
              stroke="var(--series-1)"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// One team's row: an outer bar whose LENGTH encodes total portfolio value
// (relative to the top team, so you can compare teams at a glance), and
// whose segments show its composition — cash (always gray, always first)
// plus each LONG holding, sized by its own share of that team's gross
// exposure. Short positions are liabilities, not assets — their value is
// negative, so they can't be sized as an ordinary slice of a positive bar.
// Instead they're pooled into one "Shorts" segment at the end, sized against
// the same gross-exposure denominator as everything else (so the bar still
// fills edge to edge even though net total_value is lower), with a distinct
// hatch texture rather than a new hue — per the dataviz skill, a status-like
// "this part is a liability" signal should carry its own encoding, not
// compete with the categorical holding colors.
function LeaderboardBar({ entry, maxValue }) {
  const trackWidthPct = maxValue > 0 ? (entry.total_value / maxValue) * 100 : 0;

  const longHoldings = entry.holdings.filter((h) => h.quantity >= 0);
  const shortHoldings = entry.holdings.filter((h) => h.quantity < 0);
  const shortValue = shortHoldings.reduce((sum, h) => sum + Math.abs(h.current_value), 0);

  const segments = [];
  if (entry.cash > 0 || longHoldings.length === 0) {
    segments.push({ key: 'cash', label: 'Cash', value: entry.cash, className: 'segment-cash' });
  }
  longHoldings.forEach((h, i) => {
    if (i < HOLDING_SLOT_COUNT) {
      segments.push({
        key: h.ticker,
        label: h.ticker,
        value: h.current_value,
        className: `segment-holding-${i + 1}`,
      });
    }
  });
  if (longHoldings.length > HOLDING_SLOT_COUNT) {
    // More distinct positions than the safe color-slot count — rare (would
    // mean holding 8+ different tickers at once), but fold the smallest
    // ones into one "Other" segment rather than reusing a slot or
    // generating a new hue.
    const rest = longHoldings.slice(HOLDING_SLOT_COUNT);
    const restValue = rest.reduce((sum, h) => sum + h.current_value, 0);
    segments.push({
      key: 'other',
      label: 'Other',
      value: restValue,
      className: 'segment-other',
      title: `Other holdings (${rest.map((h) => h.ticker).join(', ')}): ${formatCurrency(restValue)}`,
    });
  }
  // Gross exposure = cash + every long + the magnitude of every short — the
  // denominator all segments (including Shorts) are sized against, so the
  // bar still reads as "100% of what this team is exposed to," not just the
  // net value that's left over after the shorts bite into it.
  const grossExposure = entry.cash
    + longHoldings.reduce((sum, h) => sum + h.current_value, 0)
    + shortValue;
  if (shortHoldings.length > 0) {
    segments.push({
      key: 'shorts',
      label: 'Shorts',
      value: shortValue,
      className: 'segment-shorts',
      title: `Short (owed back): ${shortHoldings.map((h) => `${h.ticker} ${h.quantity}`).join(', ')} — ${formatCurrency(-shortValue)}`,
    });
  }

  return (
    <div className="leaderboard-row">
      <div className="leaderboard-team-name">{entry.team}</div>
      <div className="leaderboard-track">
        <div className="leaderboard-bar" style={{ width: `${trackWidthPct}%` }}>
          {segments.map((seg) => {
            const widthPct = grossExposure > 0 ? (seg.value / grossExposure) * 100 : 0;
            if (widthPct <= 0) return null;
            return (
              <div
                key={seg.key}
                className={`leaderboard-segment ${seg.className}`}
                style={{ width: `${widthPct}%` }}
                title={seg.title || `${seg.label}: ${formatCurrency(seg.value)} (${widthPct.toFixed(0)}%)`}
              >
                {/* Direct label only where it'll actually fit — a 4-letter
                    ticker needs real room, so this is a width heuristic,
                    not a strict measurement. */}
                {widthPct >= 12 && <span className="leaderboard-segment-label">{seg.label}</span>}
              </div>
            );
          })}
        </div>
      </div>
      <div className="leaderboard-value">{formatCurrency(entry.total_value)}</div>
    </div>
  );
}

function Leaderboard({ leaderboard }) {
  const maxValue = leaderboard.reduce((max, entry) => Math.max(max, entry.total_value), 0);

  return (
    <div className="leaderboard-panel">
      <h2 className="panel-title">Leaderboard</h2>
      <p className="leaderboard-legend">
        <span className="legend-swatch legend-swatch-cash" /> cash on hand &nbsp;·&nbsp;
        <span className="legend-swatch legend-swatch-holding" /> each color is one holding &nbsp;·&nbsp;
        <span className="legend-swatch legend-swatch-shorts" /> shorts (owed back, not an asset)
      </p>
      <div className="leaderboard-rows">
        {leaderboard.map((entry) => (
          <LeaderboardBar key={entry.team} entry={entry} maxValue={maxValue} />
        ))}
      </div>
    </div>
  );
}

function useCycler(items, intervalMs) {
  const [index, setIndex] = useState(0);
  const indexRef = useRef(0);
  // The interval's closure always needs the CURRENT items (for its length
  // and to read the live array), but the effect itself must only restart on
  // a genuine content change — see the signature note below.
  const itemsRef = useRef(items);
  itemsRef.current = items;

  // Every poll hands NewsTicker a brand-new `news` array, even when the
  // round (and so the headlines) hasn't changed — a new array reference
  // every 5s, same strings inside. Keying the effect on `items` itself
  // reset the cycle back to item 0 on every poll, well inside the 6s
  // rotation, so it never visibly advanced until the round actually
  // changed. Keying on a joined signature instead means the effect only
  // restarts when the headlines themselves change.
  const signature = (items || []).join('\u0001');

  useEffect(() => {
    if (!items || items.length === 0) return undefined;
    indexRef.current = 0;
    setIndex(0);
    const id = setInterval(() => {
      const len = itemsRef.current.length;
      indexRef.current = (indexRef.current + 1) % len;
      setIndex(indexRef.current);
    }, intervalMs);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, intervalMs]);

  if (!items || items.length === 0) return null;
  return items[index % items.length];
}

function NewsTicker({ news }) {
  const items = news.map((n) => (n.ticker ? `[${n.ticker}] ${n.headline}` : n.headline));
  const current = useCycler(items, NEWS_CYCLE_MS);

  return (
    <div className="ticker news-ticker">
      <span className="ticker-label">NEWS</span>
      <span key={current} className="ticker-text">
        {current || 'Waiting for the next round…'}
      </span>
    </div>
  );
}

// Every recent trade, stacked newest-first, instead of rotating through one
// at a time — the room has the screen real estate for it, and seeing every
// agent's move at once is itself the "gossip" layer: a team going loud on
// one name is visibly called out (server-flagged via is_loud, based on how
// big that trade was relative to the team's own portfolio), while a small,
// quiet position just scrolls by like everything else.
function TradeFeedRow({ trade }) {
  const sizeNote = trade.is_loud && trade.pct_of_portfolio != null
    ? ` — ${trade.pct_of_portfolio.toFixed(0)}% of their book`
    : '';
  return (
    <div className={`feed-row ${trade.is_loud ? 'feed-row-loud' : ''}`}>
      {trade.is_loud && <span className="feed-loud-tag">📢 going loud</span>}
      <span className="feed-row-main">
        <strong>{trade.team}</strong> {trade.action.toUpperCase()} {trade.quantity} {trade.ticker}
        {sizeNote}
      </span>
      {trade.reasoning && <span className="feed-row-quote">&ldquo;{trade.reasoning}&rdquo;</span>}
    </div>
  );
}

function TradeFeed({ trades }) {
  return (
    <div className="ticker-panel trade-feed">
      <div className="ticker-label feed-panel-label">TRADING FLOOR</div>
      <div className="feed-rows">
        {trades.length === 0 && (
          <div className="feed-row feed-row-empty">No trades yet this round…</div>
        )}
        {trades.map((t, i) => (
          <TradeFeedRow key={`${t.executed_at}-${t.team}-${i}`} trade={t} />
        ))}
      </div>
    </div>
  );
}

function GameTimer({ game }) {
  const serverSeconds = game ? game.seconds_until_next_advance : null;
  const paused = game ? game.paused : false;
  const [secondsLeft, setSecondsLeft] = useState(serverSeconds);

  // Resync to the server's number every time a fresh poll brings one in —
  // this is the source of truth, the local interval below just smooths the
  // countdown between polls instead of it jumping every 5s.
  useEffect(() => {
    setSecondsLeft(serverSeconds);
  }, [serverSeconds]);

  useEffect(() => {
    if (paused || secondsLeft === null) return;
    const id = setInterval(() => {
      setSecondsLeft((s) => (s === null ? null : Math.max(0, s - 1)));
    }, 1000);
    return () => clearInterval(id);
  }, [paused, secondsLeft === null]);

  if (!game) return null; // manual mode — no timer running, nothing to show

  const mm = secondsLeft !== null ? Math.floor(secondsLeft / 60) : null;
  const ss = secondsLeft !== null ? secondsLeft % 60 : null;

  return (
    <div className={`game-timer${paused ? ' game-timer-paused' : ''}`}>
      <span className="game-timer-label">{paused ? 'Paused' : 'Next round in'}</span>
      {!paused && secondsLeft !== null && (
        <span className="game-timer-clock">
          {String(mm).padStart(2, '0')}:{String(ss).padStart(2, '0')}
        </span>
      )}
      <span className="game-timer-progress">
        Round {game.rounds_consumed} / {game.total_rounds}
      </span>
    </div>
  );
}

export default function TradingSimDashboard() {
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const [transition, setTransition] = useState(null);
  const initializedRef = useRef(false);
  const seenSummaryRoundRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    async function poll() {
      try {
        const res = await fetch(DISPLAY_ENDPOINT);
        if (!res.ok) throw new Error(`Status ${res.status}`);
        const data = await res.json();
        if (cancelled) return;

        setState(data);
        setError(null);

        // Detect a NEW round transition (not just "a summary exists" — on
        // first load we've missed whatever transition already happened, so
        // we silently note it as "seen" rather than replaying it late).
        const summary = data.last_round_summary;
        if (!initializedRef.current) {
          initializedRef.current = true;
          seenSummaryRoundRef.current = summary ? summary.round_id : null;
        } else if (summary && summary.round_id !== seenSummaryRoundRef.current) {
          seenSummaryRoundRef.current = summary.round_id;
          // No fixed timeout here anymore — RoundTransitionOverlay paces
          // itself through its own slides and calls dismissTransition when
          // it's done (or the viewer clicks through to the end).
          setTransition(summary);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    }

    poll();
    const id = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  function dismissTransition() {
    setTransition(null);
  }

  if (error && !state) {
    return <div className="dashboard-root dashboard-error">Couldn't reach the market — {error}</div>;
  }
  if (!state) {
    return <div className="dashboard-root dashboard-loading">Loading market data…</div>;
  }

  return (
    <div className="dashboard-root">
      <header className="dashboard-header">
        <h1>Hackathon Trading Floor</h1>
        <div className="round-badge">
          {state.round_id ? `Round: ${state.round_id}` : 'No round open yet'}
        </div>
        <GameTimer game={state.game} />
      </header>

      <div className="dashboard-body">
        <section className="companies-grid">
          {state.companies.map((company) => (
            <CompanyCard key={company.ticker} company={company} />
          ))}
        </section>

        <aside className="dashboard-sidebar">
          <Leaderboard leaderboard={state.leaderboard} />
        </aside>
      </div>

      <footer className="dashboard-footer">
        <NewsTicker news={state.news} />
        <TradeFeed trades={state.recent_trades} />
      </footer>

      <RoundTransitionOverlay summary={transition} onDismiss={dismissTransition} />
    </div>
  );
}